// Admin side panel for one bracket match: create the panel match (server), set / undo the
// result, best-of. Every action goes to /api/brackets/:id/...; the server's refusal text
// (e.g. "the next match already has a result") is shown as is.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import { roundLabel, cardStatus, statusLabel, scoresOf, slotSource } from './bracketText';

export default function MatchPanel({ bracket, match: m, matches, teams, servers, onClose, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [confirmWin, setConfirmWin] = useState(null);
  const [serverId, setServerId] = useState('');

  const matchId = m ? m.id : null;
  useEffect(() => { setErr(''); setNote(''); setConfirmWin(null); }, [matchId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!m) return null;
  const st = cardStatus(m);
  const sideKey = m.bracket_side || 'winners';
  const stage = sideKey === 'swiss' || sideKey.startsWith('group_'); // Swiss / round robin: draws allowed, no next match
  const decided = st === 'done' || st === 'draw';
  const sc = scoresOf(m);
  const both = !!(m.team1_id && m.team2_id);
  const active = bracket.status === 'active';
  const draft = bracket.status === 'draft';
  const byId = (id) => matches.find(x => x.id === id);
  const winnerTo = m.winner_to_id ? byId(m.winner_to_id) : null;
  const loserTo = m.loser_to_id ? byId(m.loser_to_id) : null;
  const freeServers = [...(servers || [])].sort((a, b) => (a.status === 'available' ? 0 : 1) - (b.status === 'available' ? 0 : 1));

  const run = async (fn, okMsg) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const res = await fn();
      if (okMsg) setNote(typeof okMsg === 'function' ? okMsg(res) : okMsg);
      await onChanged();
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setBusy(false); setConfirmWin(null); }
  };
  const base = `/api/brackets/${bracket.id}/matches/${m.id}`;

  const side = (slot) => {
    const id = m[`team${slot}_id`];
    const tm = id ? teams[id] : null;
    return (
      <div className={`side${m.winner_id && m.winner_id === id ? ' win' : ''}`}>
        {tm ? <TeamLogo team={tm} size={44} radius={10} /> : <div style={{ width: 44, height: 44, borderRadius: 10, border: '1px dashed var(--border-default)' }} />}
        <span className="nm" title={tm ? tm.name : ''}>{tm ? tm.name : slotSource(t, matches, m, slot)}</span>
        <span className="sd">{tm && tm.seed ? t('bracket.seedN', 'Seed {{n}}', { n: tm.seed }) : ' '}</span>
      </div>
    );
  };

  return (
    <>
      <div className="br-panel-bg" onClick={onClose} />
      <aside className="br-panel" role="dialog" aria-label={m.code}>
        <div className="br-panel-head">
          <div>
            <div className="t">{m.code} · {roundLabel(t, m.round_name, m.round_number)}</div>
            <div className="s">
              BO{m.best_of || 1} · <span className={`st-${st}`} style={{ fontWeight: 700 }}>{statusLabel(t, st)}</span>
            </div>
          </div>
          <button className="x" onClick={onClose} aria-label={t('common.close', 'Close')}>×</button>
        </div>

        <div className="br-panel-body">
          <div className="br-vs">
            {side(1)}
            <div className="mid">{sc ? `${sc[0]} : ${sc[1]}` : 'vs'}</div>
            {side(2)}
          </div>

          {st === 'decision' && (
            <div className="br-note amber">{t('bracket.panel.decisionNote', 'The series ended level. Pick the winner below.')}</div>
          )}
          {(st === 'bye' || st === 'walkover') && (
            <div className="br-note blue">{st === 'bye'
              ? t('bracket.panel.byeNote', 'No opponent in this slot - the team goes through without playing.')
              : t('bracket.panel.walkoverNote', 'Only one team can reach this match - it is decided without playing.')}</div>
          )}

          <dl className="br-kv">
            {winnerTo && <><dt>{t('bracket.panel.winnerTo', 'Winner goes to')}</dt><dd>{winnerTo.code} · {roundLabel(t, winnerTo.round_name, winnerTo.round_number)}</dd></>}
            {loserTo && <><dt>{t('bracket.panel.loserTo', 'Loser goes to')}</dt><dd>{loserTo.code} · {roundLabel(t, loserTo.round_name, loserTo.round_number)}</dd></>}
            {!winnerTo && !m.is_third_place && !stage && sideKey !== 'losers' && <><dt>{t('bracket.panel.winnerTo', 'Winner goes to')}</dt><dd>🏆 {t('bracket.champion', 'Champion')}</dd></>}
            {m.match_id && <><dt>{t('bracket.panel.panelMatch', 'Panel match')}</dt><dd><Link to={`/admin/matches/${m.match_id}`}>#{m.match_id} · {m.match_status || '-'}</Link></dd></>}
          </dl>

          {/* create the panel match */}
          {both && !decided && st !== 'walkover' && !m.match_id && (
            <div className="br-sec">
              <div className="lbl">{t('bracket.panel.createMatch', 'Create match')}</div>
              {!active
                ? <div className="br-hint">{draft ? t('bracket.panel.startFirst', 'Start the bracket to create matches.') : t('bracket.panel.notActive', 'The bracket is not running.')}</div>
                : <>
                  <select className="br-select full" value={serverId} onChange={e => setServerId(e.target.value)}>
                    <option value="">{t('bracket.panel.pickServer', 'Pick a server…')}</option>
                    {freeServers.map(s => <option key={s.id} value={s.id}>{s.name}{s.ip && !String(s.ip).startsWith('lan.') ? ` (${s.ip}:${s.port})` : ''}{s.status ? ` - ${s.status}` : ''}</option>)}
                  </select>
                  <div className="br-row">
                    <button className="br-btn" disabled={busy || !serverId}
                      onClick={() => run(() => axios.post(`${base}/schedule`, { server_id: Number(serverId) }),
                        (res) => t('bracket.panel.created', 'Match #{{id}} created. Open it and send it to the server.', { id: res.data.match.id }))}>
                      {t('bracket.panel.createBtn', 'Create match')}
                    </button>
                  </div>
                  <div className="br-hint">{t('bracket.panel.createHint', 'Uses the season settings (veto, maps, rules). Maps are picked on the match page or in the veto.')}</div>
                </>}
            </div>
          )}

          {/* result */}
          {both && !decided && st !== 'walkover' && st !== 'live' && (draft || active) && (
            <div className="br-sec">
              <div className="lbl">{t('bracket.panel.setWinner', 'Set winner')}</div>
              {confirmWin ? (
                <>
                  <div className="br-note blue">{confirmWin === 'draw'
                    ? t('bracket.panel.confirmDraw', '{{code}} ends in a draw?', { code: m.code })
                    : t('bracket.panel.confirmWinner', '{{team}} wins {{code}}?', { team: teams[confirmWin] ? teams[confirmWin].name : '', code: m.code })}</div>
                  <div className="br-row">
                    <button className="br-btn green" disabled={busy} onClick={() => run(() => axios.put(`${base}/winner`, confirmWin === 'draw' ? { draw: true } : { winner_id: confirmWin }))}>{t('common.confirm', 'Confirm')}</button>
                    <button className="br-btn ghost" disabled={busy} onClick={() => setConfirmWin(null)}>{t('common.cancel', 'Cancel')}</button>
                  </div>
                </>
              ) : (
                <div className="br-row">
                  {[1, 2].map(s => (
                    <button key={s} className="br-btn ghost" disabled={busy} onClick={() => setConfirmWin(m[`team${s}_id`])}>
                      ✓ {teams[m[`team${s}_id`]] ? teams[m[`team${s}_id`]].name : ''}
                    </button>
                  ))}
                  {stage && <button className="br-btn ghost" disabled={busy} onClick={() => setConfirmWin('draw')}>= {t('bracket.status.draw', 'Draw')}</button>}
                </div>
              )}
              {m.match_id && st !== 'decision' && <div className="br-hint">{t('bracket.panel.autoResult', 'The result is set by itself when the panel match ends.')}</div>}
            </div>
          )}

          {decided && (draft || active || bracket.status === 'completed') && (
            <div className="br-sec">
              <div className="lbl">{t('bracket.panel.result', 'Result')}</div>
              <div className="br-row">
                <button className="br-btn danger" disabled={busy}
                  onClick={() => run(() => axios.put(`${base}/reset`), (res) => (res.data.unlinked && res.data.unlinked.length
                    ? t('bracket.panel.undoneUnlinked', 'Result removed. Match(es) #{{ids}} no longer belong to the bracket - cancel them on their page.', { ids: res.data.unlinked.join(', #') })
                    : t('bracket.panel.undone', 'Result removed.')))}>
                  {t('bracket.panel.undo', 'Undo result')}
                </button>
              </div>
              <div className="br-hint">{t('bracket.panel.undoHint', 'Only possible while the next match has not been played.')}</div>
            </div>
          )}

          {/* manual matchup: swap a team with a team of another open match (same as drag & drop) */}
          {(draft || active) && m.status !== 'completed' && !m.match_id && (m.team1_id || m.team2_id) && (() => {
            const others = [];
            for (const o of matches) {
              if (o.status === 'completed' || o.match_id) continue;
              for (const s of [1, 2]) {
                const id = o[`team${s}_id`];
                if (id && !(o.id === m.id)) others.push({ o, s, id });
              }
            }
            if (!others.length) return null;
            return (
              <div className="br-sec">
                <div className="lbl">{t('bracket.panel.matchup', 'Matchup')}</div>
                {[1, 2].filter(s => m[`team${s}_id`]).map(s => (
                  <select key={s} className="br-select full" value="" disabled={busy}
                    onChange={e => {
                      const [oid, os] = e.target.value.split(':').map(Number);
                      if (oid) run(() => axios.put(`/api/brackets/${bracket.id}/slots/swap`, { a: { match_id: m.id, slot: s }, b: { match_id: oid, slot: os } }));
                    }}>
                    <option value="">{t('bracket.panel.swapWith', 'Swap {{team}} with…', { team: teams[m[`team${s}_id`]] ? teams[m[`team${s}_id`]].name : '' })}</option>
                    {others.map(x => <option key={`${x.o.id}:${x.s}`} value={`${x.o.id}:${x.s}`}>{teams[x.id] ? teams[x.id].name : `#${x.id}`} · {x.o.code}</option>)}
                  </select>
                ))}
              </div>
            );
          })()}

          {/* best-of for this match */}
          {(st === 'waiting' || st === 'ready' || st === 'scheduled') && (draft || active) && (
            <div className="br-sec">
              <div className="lbl">{t('bracket.panel.format', 'Format')}</div>
              <div className="br-row">
                {[1, 3, 5].map(bo => (
                  <button key={bo} className={`br-btn ${Number(m.best_of || 1) === bo ? '' : 'ghost'} sm`} disabled={busy || Number(m.best_of || 1) === bo}
                    onClick={() => run(() => axios.put(`${base}/best-of`, { best_of: bo }))}>BO{bo}</button>
                ))}
              </div>
            </div>
          )}

          {note && <div className="br-note blue">{note}</div>}
          {err && <div className="br-note red">{err}</div>}
        </div>
      </aside>
    </>
  );
}
